// src/components/ArticulationSideView.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
} from "react";

/* ============================================================
   SVG LOADING + PARSING
   ============================================================ */

const DEBUG_SVG = false; // flip to true if you need logs again

async function fetchText(url, signal) {
  const r = await fetch(url, {
    signal,
    cache: "no-store", // bypass browser + SW cache
  });
  if (!r.ok) throw new Error(`Failed to fetch SVG (${r.status})`);
  return await r.text();
}

function extractSvgParts(svgText) {
  // Remove hardcoded width/height so viewBox controls scaling
  const cleaned = svgText
    .replace(/width="[^"]*"/i, "")
    .replace(/height="[^"]*"/i, "")
    // strip editor-only blocks that can break innerHTML parsing
    .replace(/<sodipodi:namedview[\s\S]*?<\/sodipodi:namedview>/gi, "")
    .replace(/<sodipodi:namedview[\s\S]*?\/>/gi, "")
    .replace(/<inkscape:perspective[\s\S]*?\/>/gi, "")
    // optional: remove defs from imported file (you already define defs below)
    .replace(/<defs[\s\S]*?<\/defs>/gi, "");

  const vbMatch = cleaned.match(/viewBox="([^"]+)"/i);
  const viewBox = vbMatch ? vbMatch[1] : `0 0 322.22221 344.44445`;

  const openTagEnd = cleaned.indexOf(">");
  const closeTagStart = cleaned.lastIndexOf("</svg");

  const inner =
    openTagEnd >= 0 && closeTagStart > openTagEnd
      ? cleaned.slice(openTagEnd + 1, closeTagStart)
      : cleaned;

  const nums = viewBox.trim().split(/[\s,]+/).map(Number);
  const vbW = Number.isFinite(nums[2]) ? nums[2] : 322.22221;

  return { viewBox, inner, vbW };
}

/* ============================================================
   SVG UTILITIES
   ============================================================ */

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

// CSS.escape fallback
function safeEscape(id) {
  if (
    typeof window !== "undefined" &&
    window.CSS &&
    typeof window.CSS.escape === "function"
  ) {
    return window.CSS.escape(id);
  }
  return String(id).replace(/([ #;?%&,.+*~\':"!^$[\]()=>|/@])/g, "\\$1");
}

function stripDisplayFromStyleAttr(el) {
  if (!el) return;
  const s = el.getAttribute("style");
  if (!s) return;
  const next = s.replace(/(?:^|;)\s*display\s*:\s*[^;]+;?/gi, "");
  const cleaned = next.replace(/^\s*;\s*|\s*;\s*$/g, "").trim();
  if (cleaned) el.setAttribute("style", cleaned);
  else el.removeAttribute("style");
}

/**
 * Stable renderer:
 * - Forces known Inkscape layers ON
 * - Removes Inkscape inline display overrides for variants
 * - Hides all variants, shows only requested ids
 */
function applyRender(svgRootEl, showIds) {
  if (!svgRootEl) return;

  const wants = new Set(showIds || []);

  // Keep base layers visible (never hide these)
  ["layer4", "layer6", "layer1", "layer8"].forEach((id) => {
    const layer = svgRootEl.querySelector(`#${safeEscape(id)}`);
    if (layer) layer.style.display = "inline";
  });

  // Only toggle variant groups (not parent layers)
  const variants = svgRootEl.querySelectorAll(
    'g[id^="upper_jaw_"], g[id^="lower_jaw_"], g[id^="vocal_folds_"]'
  );

  variants.forEach((el) => {
    if (!el?.id) return;

    // Inkscape likes to set style="display:..." inline: strip it once, then we own it.
    stripDisplayFromStyleAttr(el);

    el.style.display = wants.has(el.id) ? "inline" : "none";
  });

  if (DEBUG_SVG) {
    (showIds || []).forEach((id) => {
      const el = svgRootEl.querySelector(`#${safeEscape(id)}`);
      if (!el) console.warn("Missing SVG id:", id);
    });
  }
}

/**
 * Anchor reader: uses getCTM() so nested transforms are respected.
 */
function readAnchor(svgRootEl, id) {
  const el = svgRootEl?.querySelector(`#${safeEscape(id)}`);
  if (!el) return null;

  const ctm = el.getCTM?.();
  if (ctm && Number.isFinite(ctm.e) && Number.isFinite(ctm.f)) {
    return { x: ctm.e, y: ctm.f };
  }
  return null;
}

/* ============================================================
   TIMELINE MODEL + FALLBACK
   ============================================================ */

const DURATION_SCALE = 10;
const CYCLE_PAUSE_MS = 220; // noticeable pause between full loops (tweak 150–450)

function burstDuration(type) {
  if (type === "strong") return 160;
  if (type === "weak") return 110;
  return 140;
}

function burstStrength(type) {
  if (type === "strong") return 1.0;
  if (type === "weak") return 0.6;
  return 0;
}

function getStageDurationMs(target, stage, fallback = 650) {
  const d = target?.timing?.durations || {};
  const v = d?.[stage];
  return Number.isFinite(v) ? v : fallback;
}

function normalizeTimeline(target) {
  const r = target?.render || {};

  // Preferred
  if (Array.isArray(r.timeline) && r.timeline.length) {
    return r.timeline
      .filter(Boolean)
      .map((f, i) => ({
        phase: f.phase || `frame_${i + 1}`,
        show: Array.isArray(f.show) ? f.show : [],
        dur: Number.isFinite(f.dur) ? f.dur : 0,
        ...(f.burst ? { burst: f.burst } : {}),
        ...(typeof f.voicing === "boolean" ? { voicing: f.voicing } : {}),
      }))
      .filter((f) => f.show.length && f.dur > 0);
  }

  // Legacy fallback
  const stages = ["rest", "closure", "release", "static"];
  const frames = stages
    .map((stage) => {
      const frame = r?.[stage];
      if (!frame?.show) return null;
      return {
        phase: stage,
        show: frame.show,
        dur: getStageDurationMs(target, stage, 650),
        ...(frame?.burst ? { burst: frame.burst } : {}),
        ...(typeof frame?.voicing === "boolean" ? { voicing: frame.voicing } : {}),
      };
    })
    .filter(Boolean);

  return frames.length ? frames : [{ phase: "empty", show: [], dur: 650 }];
}

function pillLabel(phase) {
  if (!phase) return "Frame";
  const p = String(phase);
  if (p === "rest") return "Rest";
  if (p === "closure") return "Closure";
  if (p === "release") return "Release";
  if (p === "static") return "Static";
  return p.replace(/_/g, " ");
}

function pillClass(phase) {
  if (phase === "rest") return "bg-slate-100 text-slate-700 border-slate-200";
  if (phase === "closure") return "bg-amber-50 text-amber-800 border-amber-200";
  if (phase === "release") return "bg-sky-50 text-sky-800 border-sky-200";
  if (phase === "static") return "bg-slate-50 text-slate-700 border-slate-200";
  return "bg-indigo-50 text-indigo-800 border-indigo-200";
}

/* ============================================================
   HOOK: prefers-reduced-motion
   ============================================================ */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;

    const update = () => setReduced(!!mq.matches);
    update();

    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  return reduced;
}

/* ============================================================
   COMPONENT
   ============================================================ */

export default function ArticulationSideView({
  baseSvgSrc = `${process.env.REACT_APP_USER_API_URL}/uploads/diagrams/sounds.svg?v=dev`,
  target,
  size = "normal", // "normal" | "large"
  fill = false,
  align = "center", // "center" | "top"
  onOpenModal = null,
  enableModalClick = true,
  showHint = true,
}) {
  const svgRef = useRef(null);
  const reducedMotion = usePrefersReducedMotion();

  const [svgInner, setSvgInner] = useState(null);
  const [svgStatus, setSvgStatus] = useState("loading"); // loading | ready | error

  // Timeline playback
  const frames = useMemo(() => normalizeTimeline(target), [target]);
  const [frameIx, setFrameIx] = useState(0);
  const [playing, setPlaying] = useState(true);

  // Anchors
  const [mouthPos, setMouthPos] = useState(null);
  const [glottisPos, setGlottisPos] = useState(null);

  // Burst/voicing visuals
  const [flowT, setFlowT] = useState(0);
  const [burstActive, setBurstActive] = useState(false);
  const [voiceWindowActive, setVoiceWindowActive] = useState(false);

  // Tempo slider
  const [speedMs, setSpeedMs] = useState(650);
  const tempo = useMemo(() => speedMs / 650, [speedMs]);

  const current =
    frames?.[Math.max(0, Math.min(frameIx, frames.length - 1))] || null;

  const canAnimate = frames.length > 1;

  // ✅ detect vocal fold state from the current frame
  const isVocalClosed = !!current?.show?.includes("vocal_folds_closed");

  // Ensure frame index is valid when target changes.
  useEffect(() => {
    setFrameIx(0);
    if (!reducedMotion && canAnimate) setPlaying(true);
    else setPlaying(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  // Reduced motion: auto-pause
  useEffect(() => {
    if (reducedMotion) setPlaying(false);
  }, [reducedMotion]);

  /* ------------------------------------------------------------
     Inject CSS keyframes for vocal fold vibration (once)
  ------------------------------------------------------------ */
  useEffect(() => {
    const id = "vf-vibrate-style";
    if (document.getElementById(id)) return;

    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      @keyframes vocalFoldVibrate {
        0%   { transform: translate(0px, 0px) scaleY(1); }
        25%  { transform: translate(0.35px, -0.25px) scaleY(0.96); }
        50%  { transform: translate(-0.35px, 0.25px) scaleY(1); }
        75%  { transform: translate(0.25px, 0.35px) scaleY(0.965); }
        100% { transform: translate(0px, 0px) scaleY(1); }
      }
        @keyframes noteFloat {
  0%   { transform: translateY(0px); opacity: 0.9; }
  50%  { transform: translateY(-8px); opacity: 1; }
  100% { transform: translateY(0px); opacity: 0.9; }
}
    `;
    document.head.appendChild(style);

    return () => {
      // optional: keep it; remove if you prefer strict cleanup
      // document.head.removeChild(style);
    };
  }, []);

  /* ------------------------------------------------------------
     Load SVG (abortable)
  ------------------------------------------------------------ */
  useEffect(() => {
    const ac = new AbortController();
    let alive = true;

    setSvgStatus("loading");

    (async () => {
      try {
        const txt = await fetchText(baseSvgSrc, ac.signal);
        if (!alive) return;
        setSvgInner(extractSvgParts(txt));
        setSvgStatus("ready");
      } catch (e) {
        if (!alive) return;
        if (String(e?.name) === "AbortError") return;
        setSvgInner(null);
        setSvgStatus("error");
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [baseSvgSrc]);

  /* ------------------------------------------------------------
     Apply current frame rendering + read anchors
  ------------------------------------------------------------ */
  useLayoutEffect(() => {
    if (!svgInner) return;
    const root = svgRef.current;
    if (!root) return;

    applyRender(root, current?.show || []);

    // Anchors
    setMouthPos(readAnchor(root, "anchor_mouth_exit"));
    setGlottisPos(readAnchor(root, "anchor_glottis"));

    // ✅ Vocal fold styling + vibration when CLOSED

// VOCAL FOLDS: color + vibration
// VOCAL FOLDS: blue + vibration
const closed = root.querySelector("#vocal_folds_closed");
const open = root.querySelector("#vocal_folds_open");

function forceBlue(group) {
  if (!group) return;

  group.querySelectorAll("*").forEach((el) => {
    // 1. Remove stroke/fill from inline style
    const styleAttr = el.getAttribute("style");
    if (styleAttr) {
      const cleaned = styleAttr
        .replace(/stroke\s*:\s*[^;]+;?/gi, "")
        .replace(/fill\s*:\s*[^;]+;?/gi, "")
        .trim();
      if (cleaned) el.setAttribute("style", cleaned);
      else el.removeAttribute("style");
    }

    // 2. Remove attributes
    el.removeAttribute("stroke");
    el.removeAttribute("fill");

    // 3. Apply blue via style (this now wins)
    el.style.stroke = "#2563eb";
    el.style.fill = "none";
  });
}

// Apply to both variants so no flashing
forceBlue(closed);
forceBlue(open);

if (closed) {
  closed.style.transformBox = "fill-box";
  closed.style.transformOrigin = "center";
  closed.style.willChange = "transform";

  closed.style.animation = isVocalClosed
    ? "vocalFoldVibrate 0.09s infinite linear"
    : "";
}
  }, [svgInner, current, isVocalClosed]);

  /* ------------------------------------------------------------
     Playback loop (timeline)
  ------------------------------------------------------------ */
 useEffect(() => {
  if (!playing) return;
  if (reducedMotion) return;
  if (!canAnimate) return;

  const base = Number.isFinite(current?.dur) ? current.dur : 650;
  const frameMs = Math.max(10, Math.round(base * DURATION_SCALE * tempo));

  const isLast = frameIx === frames.length - 1;
  const pauseMs = isLast ? Math.round(CYCLE_PAUSE_MS * tempo) : 0;
  const ms = frameMs + pauseMs;

  const t = setTimeout(() => {
    setFrameIx((i) => (i + 1) % frames.length);
  }, ms);

  return () => clearTimeout(t);
}, [
  playing,
  reducedMotion,
  canAnimate,
  current?.dur,
  frames.length,
  frameIx,
  tempo,
]);
  const stepFrame = useCallback(() => {
    setPlaying(false);
    setFrameIx((i) => (i + 1) % frames.length);
  }, [frames.length]);

  /* ------------------------------------------------------------
     One-shot window (burst) + optional voicing window
  ------------------------------------------------------------ */
  const burst = current?.burst || null;
  const wantsVoicing = !!current?.voicing;
  const voicing = wantsVoicing && (burst ? voiceWindowActive : true);

  useEffect(() => {
    let raf;

    const wantsBurstNow = !!burst;
    const wantsVoiceNow = !!wantsVoicing;

    if (!wantsBurstNow && !wantsVoiceNow) {
      setBurstActive(false);
      setVoiceWindowActive(false);
      setFlowT(0);
      return;
    }

    if (!wantsBurstNow) {
      setBurstActive(false);
      setFlowT(0);
      setVoiceWindowActive(true);
      return () => {};
    }

    const D =
      (Number.isFinite(target?.timing?.durations?.burst) &&
        target.timing.durations.burst) ||
      (burst ? burstDuration(burst) : 140);

    setBurstActive(true);
    setVoiceWindowActive(wantsVoiceNow);
    setFlowT(0);

    const start = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - start) / D);
      setFlowT(t);

      if (t < 1) raf = requestAnimationFrame(tick);
      else {
        setBurstActive(false);
        setVoiceWindowActive(false);
        setFlowT(0);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [frameIx, burst, wantsVoicing, target]);

  /* ------------------------------------------------------------
     Scaling
  ------------------------------------------------------------ */
  const S = useMemo(() => {
    const w = svgInner?.vbW || 322.22221;
    return w / 322.22221;
  }, [svgInner?.vbW]);

  /* ------------------------------------------------------------
     Airflow geometry
  ------------------------------------------------------------ */
  const flow = useMemo(() => {
    const k = burstStrength(burst);
    const fade = 1 - flowT;

    const baseLen = 110 * S;
    const amp = 11 * S;
    const gap = 9 * S;
    const stroke = 4.5 * S;

    const opacity = Math.max(0, 0.95 * k * fade);
    const phase = flowT * Math.PI * 2;

    const lines = [0, 1, 2].map((i) => {
      const y = i * gap - gap;
      const len = baseLen * (0.82 + 0.18 * i);
      const a = amp * (0.85 + 0.25 * i);
      const p = phase + i * 1.1;

      return {
        d: `M 0 ${y}
            C ${-0.3 * len} ${y + Math.sin(p) * a},
              ${-0.6 * len} ${y + Math.sin(p + 1.4) * a},
              ${-len} ${y + Math.sin(p + 2.6) * a}`,
      };
    });

    return { lines, stroke, opacity };
  }, [S, burst, flowT]);

  /* ------------------------------------------------------------
     Voicing geometry (arrow)
  ------------------------------------------------------------ */
  const voice = useMemo(
    () => ({
      shaftH: 38 * S,
      shaftW: 5.2 * S,
      headW: 15 * S,
      headH: 15 * S,
      wiggleDx: 1.6 * S,
      wiggleDy: 2.4 * S,
    }),
    [S]
  );

  const canOpenModal = enableModalClick && typeof onOpenModal === "function";
  const openModal = useCallback(() => {
    if (canOpenModal) onOpenModal();
  }, [canOpenModal, onOpenModal]);

  const preserve = align === "top" ? "xMidYMin meet" : "xMidYMid meet";

  const heightClass = fill
    ? "h-full"
    : size === "large"
    ? "h-[min(72vh,720px)]"
    : "h-[320px]";

  const phase = current?.phase || "";
  const phaseLabel = pillLabel(phase);

  return (
    <div className="w-full">
      {/* Controls only in normal view */}
      {size !== "large" && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span
              className={cx(
                "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold",
                pillClass(phase)
              )}
              title="Current frame"
            >
              {phaseLabel}
            </span>

            <span
              className={cx(
                "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold",
                playing
                  ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                  : "bg-slate-50 text-slate-700 border-slate-200"
              )}
              title={playing ? "Animation playing" : "Animation paused"}
            >
              <span
                className={cx(
                  "h-2 w-2 rounded-full",
                  playing ? "bg-emerald-500" : "bg-slate-400"
                )}
              />
              {playing ? "Playing" : "Paused"}
            </span>

            <span className="text-[11px] font-semibold text-slate-500">
              {frames.length > 0 ? `${frameIx + 1}/${frames.length}` : ""}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={stepFrame}
              className="rounded-xl border bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 active:scale-[0.99]"
              title="Step to next frame"
              disabled={frames.length <= 1}
            >
              Step
            </button>

            <button
              type="button"
              onClick={() => setPlaying((v) => !v)}
              className={cx(
                "rounded-xl border px-3 py-2 text-xs font-semibold active:scale-[0.99]",
                playing
                  ? "bg-sky-600 text-white border-sky-600 hover:bg-sky-700"
                  : "bg-white text-slate-700 hover:bg-slate-50"
              )}
              title={
                reducedMotion
                  ? "Disabled by Reduced Motion setting"
                  : canAnimate
                  ? "Play/pause animation"
                  : "This target is a single frame"
              }
              disabled={reducedMotion || !canAnimate}
            >
              {playing ? "Pause" : "Play"}
            </button>

            <div className="hidden sm:flex items-center gap-2 rounded-xl border bg-white px-3 py-2">
              <span className="text-[11px] font-semibold text-slate-600">
                Speed
              </span>
              <input
                type="range"
                min={420}
                max={900}
                step={10}
                value={speedMs}
                onChange={(e) => setSpeedMs(parseInt(e.target.value, 10))}
                className="w-28"
                aria-label="Animation speed"
                disabled={reducedMotion || !canAnimate}
              />
            </div>
          </div>
        </div>
      )}

      {/* STAGE CARD */}
      <div
        className={cx(
          "relative overflow-hidden rounded-3xl border",
          "bg-white shadow-[0_18px_50px_rgba(15,23,42,0.10)] ring-1 ring-black/5",
          heightClass,
          canOpenModal ? "cursor-pointer" : ""
        )}
        role={canOpenModal ? "button" : undefined}
        tabIndex={canOpenModal ? 0 : undefined}
        onClick={() => openModal()}
        onKeyDown={(e) => {
          if (!canOpenModal) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openModal();
          }
        }}
        aria-label={canOpenModal ? "Open articulation diagram in modal" : undefined}
      >
        {/* Glow background */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-28 -top-28 h-72 w-72 rounded-full bg-sky-200/60 blur-3xl" />
          <div className="absolute -right-28 -bottom-28 h-72 w-72 rounded-full bg-indigo-200/60 blur-3xl" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-white/0 to-slate-50/50" />
        </div>

        {/* Loading / Error overlays */}
        {svgStatus !== "ready" && (
          <div className="absolute inset-0 z-20 flex items-center justify-center p-6">
            <div className="w-full max-w-sm rounded-2xl border bg-white/80 p-4 text-center shadow-sm backdrop-blur">
              {svgStatus === "loading" ? (
                <>
                  <div className="mx-auto h-10 w-10 animate-pulse rounded-full bg-slate-200" />
                  <div className="mt-3 text-sm font-semibold text-slate-700">
                    Loading diagram…
                  </div>
                  <div className="mt-2 space-y-2">
                    <div className="h-2 w-full animate-pulse rounded bg-slate-200" />
                    <div className="h-2 w-5/6 animate-pulse rounded bg-slate-200" />
                    <div className="h-2 w-3/4 animate-pulse rounded bg-slate-200" />
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-semibold text-slate-800">
                    Diagram failed to load
                  </div>
                  <div className="mt-1 text-xs text-slate-600">
                    Check the file URL or network. (baseSvgSrc)
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* SVG AREA */}
        <div className="relative z-10 h-full w-full p-3">
          <div className="h-full w-full rounded-2xl border bg-white/70 backdrop-blur shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
            <svg
              ref={svgRef}
              viewBox={svgInner?.viewBox || `0 0 322.22221 344.44445`}
              preserveAspectRatio={preserve}
              style={{ width: "100%", height: "100%", display: "block" }}
            >
              <defs>
                <filter id="burstGlow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="2.4" result="blur" />
                  <feColorMatrix
                    in="blur"
                    type="matrix"
                    values="
                      1 0 0 0 0
                      0 1 0 0 0
                      0 0 1 0 0
                      0 0 0 0.7 0"
                    result="glow"
                  />
                  <feMerge>
                    <feMergeNode in="glow" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>

                <filter id="voiceGlow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="1.8" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>

                <linearGradient id="burstGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor="#60a5fa" />
                  <stop offset="1" stopColor="#2563eb" />
                </linearGradient>
              </defs>

              {svgInner && <g dangerouslySetInnerHTML={{ __html: svgInner.inner }} />}

              {/* Burst */}
              {burstActive && mouthPos && (
                <g
                  transform={`translate(${mouthPos.x} ${mouthPos.y})`}
                  opacity={flow.opacity}
                  filter="url(#burstGlow)"
                >
                  {flow.lines.map((ln, i) => (
                    <path
                      key={i}
                      d={ln.d}
                      fill="none"
                      stroke="url(#burstGrad)"
                      strokeWidth={flow.stroke}
                      strokeLinecap="round"
                    />
                  ))}
                </g>
              )}

              {/* Voicing arrow (kept as-is) */}
              {voicing && glottisPos && (
  <foreignObject
    x={glottisPos.x - 20}
    y={glottisPos.y - 50}
    width="60"
    height="60"
  >
    <div
      style={{
        fontSize: "28px",
        animation: "noteFloat 0.9s infinite ease-in-out",
        transformOrigin: "center",
        textAlign: "center",
        userSelect: "none",
      }}
    >
      🎵
    </div>
  </foreignObject>
)}
            </svg>
          </div>

          {/* Overlay hint */}
          {canOpenModal && showHint && (
            <div className="pointer-events-none absolute bottom-4 left-0 right-0 text-center text-[11px] font-medium text-slate-500">
              Click to expand
            </div>
          )}
        </div>
      </div>
    </div>
  );
}